import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';

export default function ArticleSection() {
  const featuredArticle = articles.find(a => a.isFeatured);
  const otherArticles = articles.filter(a => !a.isFeatured).slice(0, 2);

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-widest mb-3">
              &bull; Expert Guides
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15]">
              Expert Buying Guides
            </h2>
          </div>
          <p className="text-sm text-foreground-600 max-w-md leading-relaxed">
            Dive deep into our curated guides covering every aspect of Hipobuy spreadsheet shopping — from beginner basics to advanced product research.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {featuredArticle && (
            <Link
              to={`/${featuredArticle.slug}`}
              className="lg:col-span-2 group block relative rounded-3xl overflow-hidden bg-primary-500 min-h-[360px] md:min-h-[420px] cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Artistic%20moody%20product%20photography%20of%20a%20laptop%20displaying%20colorful%20spreadsheet%20data%20surrounded%20by%20stylish%20sneakers%20and%20fashion%20accessories%20in%20dramatic%20warm%20lighting%2C%20rich%20dark%20amber%20tones%2C%20editorial%20luxury%20lifestyle%20aesthetic%2C%20deep%20shadows%2C%20sophisticated%20composition&width=1000&height=650&seq=article-featured-v2&orientation=landscape"
                alt={featuredArticle.title}
                className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/90 via-primary-900/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <span className="inline-block px-3 py-1 bg-background-50/20 backdrop-blur-sm rounded-full text-xs font-semibold text-background-50 mb-3 whitespace-nowrap">
                  {featuredArticle.category}
                </span>
                <h3 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-background-50 leading-tight mb-3">
                  {featuredArticle.title}
                </h3>
                <p className="text-sm text-background-200 leading-relaxed max-w-lg line-clamp-2">
                  {featuredArticle.excerpt}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 rounded-full text-sm font-semibold text-primary-500 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap">
                  Read Guide
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {otherArticles.map((a) => (
            <Link
              key={a.slug}
              to={`/${a.slug}`}
              className="group block bg-background-100 rounded-3xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="relative h-48 md:h-56 overflow-hidden">
                <img
                  src={
                    a.slug === 'hipobuy-spreadsheet-shoes'
                      ? 'https://readdy.ai/api/search-image?query=Beautiful%20premium%20sneakers%20on%20a%20warm%20terracotta%20surface%20with%20soft%20natural%20sunlight%20casting%20delicate%20shadows%2C%20editorial%20sneaker%20photography%2C%20clean%20minimal%20composition%2C%20warm%20earthy%20tones%2C%20no%20faces%2C%20texture%20details%20visible&width=600&height=420&seq=article-card-shoes-v2&orientation=landscape'
                      : 'https://readdy.ai/api/search-image?query=Gorgeous%20flat%20lay%20of%20neatly%20folded%20premium%20hoodies%20and%20t-shirts%20in%20warm%20earth%20tones%20on%20cream%20linen%2C%20soft%20natural%20lighting%20from%20window%2C%20editorial%20fashion%20catalog%20aesthetic%2C%20clean%20minimal%20composition%2C%20fabric%20texture%20visible%2C%20harmonious%20warm%20palette&width=600&height=420&seq=article-card-clothing-v2&orientation=landscape'
                  }
                  alt={a.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-500 rounded-full text-xs font-semibold text-background-50 whitespace-nowrap">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-sparkling-line text-[10px]"></i>
                    </div>
                    New
                  </span>
                </div>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 leading-snug mb-2 group-hover:text-primary-500 transition-colors duration-200">
                  {a.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                  {a.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-500">{a.date}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-500 whitespace-nowrap">
                    Learn More
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/hipobuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 border border-background-200/70 rounded-full text-sm font-semibold text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            View All Guides
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}